import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type SimplifiedOptionAdminAsset = {
  id: number;
  ticker: string;
  name: string;
  corresponding_spot_asset: number;
  option_type: 0 | 1;
  issued_delta: number;
  contract_multiplier: number;
  variable_currency: number;
  underlying_kind: 'EQUITY' | 'BOND';
  quote_convention: 'CURRENCY_PER_SHARE' | 'BOND_PRICE_PER_100_FACE';
};

export type SimplifiedOptionHistoryAdminRow = {
  id: number;
  simplified_option: number;
  timestamp: string;
  strike: number | null;
  annualized_implied_volatility: number | null;
  basis_points: number | null;
  source?: string;
  source_reference?: string;
};

export type SimplifiedOptionHistoryWrite = Omit<
  SimplifiedOptionHistoryAdminRow,
  'id'
> & { id?: number };

export type SimplifiedOptionHistoryUploadResult = {
  created: number;
  rows: SimplifiedOptionHistoryAdminRow[];
};

export type SimplifiedOptionAuditKind =
  | 'issuance_quotes'
  | 'order_snapshots'
  | 'settlement_orders';

export type SimplifiedOptionAuditFilters = {
  simplified_option_id?: number;
  session_id?: number;
  t0_scenario_date?: string;
  expiry?: string;
  option_type?: 0 | 1;
  status?: number | 'PENDING' | 'COMPLETED' | 'FAILED';
  limit?: number;
};

/** Flattens nested DRF validation maps so CSV errors identify their exact row and field. */
const flattenAdminDataError = (value: unknown, path: string): string[] => {
  if (Array.isArray(value)) {
    if (value.every(item => item == null || typeof item !== 'object')) {
      const message = value.map(String).join(' ');
      return [path ? `${path}: ${message}` : message];
    }
    return value.flatMap((item, index) =>
      flattenAdminDataError(item, path ? `${path}.${index}` : String(index)),
    );
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([field, nestedValue]) =>
        flattenAdminDataError(nestedValue, path ? `${path}.${field}` : field),
    );
  }
  return [path ? `${path}: ${String(value)}` : String(value)];
};

/** Converts DRF validation payloads into one actionable admin error message. */
const adminDataError = (result: unknown) => {
  const details =
    typeof result === 'string' || (result && typeof result === 'object')
      ? flattenAdminDataError(result, '').filter(Boolean).join(' ')
      : '';
  return details || 'The simplified-options admin request failed.';
};

/** Runs one PM-admin request and rejects non-2xx responses before UI state is changed. */
const adminRequest = async <
  Result,
  Payload extends object = Record<string, never>,
>(
  path: string,
  method: 'GET' | 'POST',
  data?: Payload,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  if (String(response.status)[0] !== '2') {
    throw new Error(adminDataError(response.result));
  }
  return response.result as Result;
};

/** Lists only class-19 assets for curve selection in PM's custom admin UI. */
export const fetchSimplifiedOptionsAdmin = () =>
  adminRequest<SimplifiedOptionAdminAsset[]>(
    '/admin/fetch_simplified_options',
    'GET',
  );

/** Loads one option's sparse strike, volatility, and BP curve for admin review. */
export const fetchSimplifiedOptionHistoryAdmin = (simplifiedOptionId: number) =>
  adminRequest<
    SimplifiedOptionHistoryAdminRow[],
    { simplified_option_id: number }
  >('/admin/fetch_simplified_option_history', 'GET', {
    simplified_option_id: simplifiedOptionId,
  });

/** Creates one source curve observation so runtime pricing can resolve it as-of T0/T1. */
export const createSimplifiedOptionHistoryAdmin = (
  payload: SimplifiedOptionHistoryWrite,
) =>
  adminRequest<SimplifiedOptionHistoryAdminRow, SimplifiedOptionHistoryWrite>(
    '/admin/create_simplified_option_history',
    'POST',
    payload,
  );

/** Updates one curve observation while preserving the canonical history model. */
export const updateSimplifiedOptionHistoryAdmin = (
  payload: SimplifiedOptionHistoryWrite & { id: number },
) =>
  adminRequest<
    SimplifiedOptionHistoryAdminRow,
    SimplifiedOptionHistoryWrite & { id: number }
  >('/admin/update_simplified_option_history', 'POST', payload);

/** Uploads one selected option's canonical CSV so admins can populate its sparse curve in bulk. */
export const uploadSimplifiedOptionHistoryAdmin = (
  simplifiedOptionId: number,
  file: File,
) => {
  const payload = new FormData();
  payload.append('simplified_option_id', String(simplifiedOptionId));
  payload.append('file', file, file.name);
  return adminRequest<SimplifiedOptionHistoryUploadResult, FormData>(
    '/admin/upload_simplified_option_history',
    'POST',
    payload,
  );
};

/** Retrieves one bounded immutable audit stream for operational inspection. */
export const fetchSimplifiedOptionAuditAdmin = (
  kind: SimplifiedOptionAuditKind,
  filters: SimplifiedOptionAuditFilters,
) =>
  adminRequest<Record<string, unknown>[], typeof filters>(
    `/admin/fetch_simplified_option_${kind}`,
    'GET',
    filters,
  );
